import React from 'react';
import { Autocomplete, Paper, TextField, styled } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Colors } from 'library';

const US_STATES = [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'District of Columbia',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
];

interface StateInputProps {
    value: string | null;
    onChange: (value: string | null) => void;
    id?: string;
    placeholder?: string;
    fullWidth?: boolean;
    disabled?: boolean;
    error?: string;
}

export function StateInput({
    value,
    onChange,
    id,
    placeholder = 'Search for a state...',
    fullWidth,
    disabled,
    error,
}: StateInputProps) {
    const field = (
        <Autocomplete
            options={US_STATES}
            value={value}
            onChange={(_event, newValue) => onChange(newValue)}
            disabled={disabled}
            fullWidth={fullWidth}
            autoHighlight
            openOnFocus
            popupIcon={<ExpandMore />}
            PaperComponent={StyledPaper}
            sx={{
                '& .MuiAutocomplete-popupIndicator': { color: Colors.textSecondary },
                '& .MuiAutocomplete-clearIndicator': { color: Colors.textSecondary },
            }}
            renderInput={(params) => (
                <StyledTextField
                    {...params}
                    placeholder={placeholder}
                    error={!!error}
                    inputProps={{ ...params.inputProps, id }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
                return (
                    <Option key={key} {...rest}>
                        {option}
                    </Option>
                );
            }}
        />
    );

    return (
        <Wrapper fullWidth={fullWidth}>
            {field}
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </Wrapper>
    );
}

const StyledTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        fontSize: 14,
        color: Colors.primary,
        backgroundColor: Colors.surface,
        borderRadius: 10,
        padding: 0,
        '& fieldset': { borderColor: Colors.border },
        '&:hover fieldset': { borderColor: Colors.border },
        '&.Mui-focused fieldset': { borderColor: Colors.textSecondary, borderWidth: 1 },
        '&.Mui-error fieldset': { borderColor: Colors.error },
    },
    '& .MuiAutocomplete-input': {
        padding: '10px 32px 10px 14px !important',
    },
    '& .MuiInputBase-input::placeholder': {
        color: Colors.border,
        opacity: 1,
    },
});

const Option = styled('li')({
    fontSize: 14,
    padding: '7px 14px',
    margin: '0 8px',
    minHeight: 'unset',
    borderRadius: 8,
    color: Colors.primary,
    '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
    },
    '&.Mui-focused, &:hover, &[aria-selected="true"]:hover, &[aria-selected="true"].Mui-focused': {
        backgroundColor: `${Colors.secondary} !important`,
        color: Colors.white,
    },
});

const StyledPaper = styled(Paper)({
    borderRadius: 10,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    marginTop: 4,
});

const Wrapper = styled('div')<{ fullWidth?: boolean }>(({ fullWidth }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    width: fullWidth ? '100%' : 'auto',
}));

const ErrorMessage = styled('span')({
    fontSize: 12,
    color: Colors.error,
});
